import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  ProfileMe,
  PsychAnswersInput,
  SearchPrefsInput,
} from "@parble/types";

const KEY = ["profile", "me"] as const;

export function useProfileMe() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<ProfileMe>("/api/profile/me"),
    retry: (count, err) => {
      if (
        err instanceof Error &&
        "status" in err &&
        typeof (err as { status?: number }).status === "number" &&
        (err as { status: number }).status === 404
      ) {
        return false;
      }
      return count < 2;
    },
  });
}

export function usePsychQuestions() {
  return useQuery({
    queryKey: ["psych", "questions"],
    queryFn: () =>
      api.get<Array<{ dimension: string; question: string }>>("/api/psych"),
    staleTime: Infinity,
  });
}

export function useSavePsych() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (answers: PsychAnswersInput) =>
      api.put<{ stored: number; complete: boolean }>("/api/psych", answers),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSavePrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: SearchPrefsInput) =>
      api.put<{ ok: true }>("/api/prefs", prefs),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUploadPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(
        `${
          (import.meta as unknown as { env?: { VITE_BACKEND_URL?: string } })
            .env?.VITE_BACKEND_URL ?? ""
        }/api/photos`,
        { method: "POST", body: form, credentials: "include" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          body?.error?.message ?? `Upload fehlgeschlagen (${res.status})`,
        );
      }
      return (await res.json()).data as {
        id: string;
        status: string;
        url: string;
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["photos"] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeletePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (photoId: string) => api.delete(`/api/photos/${photoId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["photos"] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function usePhotos() {
  return useQuery({
    queryKey: ["photos"],
    queryFn: () =>
      api.get<
        Array<{
          id: string;
          order: number;
          status: string;
          url: string;
          width: number;
          height: number;
        }>
      >("/api/photos"),
  });
}
